(() => {
  const SECTION_SELECTOR    = '.customers-also-bought';
  const ITEM_SELECTOR       = '[data-customers-also-bought-item]';
  const LIST_ID             = 'customers_also_bought';
  const LIST_NAME           = 'Customers Also Bought';
  const TITLE_VARIANT_SPLIT = /\s+-\s+/;

  // ── Shared utilities (window.DnkTracking) ───────────────────────────────────
  const T = (window.DnkTracking = window.DnkTracking || {});

  if (!T.getClientId) {
    const CLIENT_ID_STORAGE_KEY = 'themex-client-id';
    T.getClientId = () => {
      const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (gaCookie) {
        const parts = decodeURIComponent(gaCookie[1]).split('.');
        if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
      }
      let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
      if (!clientId) {
        clientId = crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
      }
      return clientId;
    };
  }

  if (!T.getExpVariantString) {
    T.getExpVariantString = () => window.dnkExperimentVariant || null;
  }

  if (!T.getCurrency) {
    T.getCurrency = () =>
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
  }

  if (!T.toMajorUnits) {
    T.toMajorUnits = (paise) => Math.round(Number(paise || 0)) / 100;
  }

  const { getClientId, getExpVariantString, getCurrency, toMajorUnits } = T;

  // ── CAB-specific helpers ────────────────────────────────────────────────────
  const parseVariantId = (raw) => {
    if (!raw) return 0;
    const str = String(raw).trim();
    return str.includes('/') ? Number(str.split('/').pop()) : Number(str);
  };

  const cardToItem = (card) => {
    const titleEl        = card.querySelector('.customers-also-bought__title');
    const variantTitleEl = card.querySelector('[data-variant-title]');

    let itemVariant = variantTitleEl ? variantTitleEl.textContent.trim() : '';
    let itemName    = titleEl ? titleEl.textContent.trim() : null;
    if (!itemVariant && titleEl) {
      const parts = titleEl.textContent.trim().split(TITLE_VARIANT_SPLIT);
      if (parts.length > 1) {
        itemName    = parts[0].trim();
        itemVariant = parts.slice(1).join(' - ').trim();
      }
    }

    return {
      item_id:       parseVariantId(card.dataset.variantId),
      item_name:     itemName,
      item_variant:  itemVariant || null,
      item_category: card.dataset.productType || 'NA',
      price:         Number(card.dataset.price || 0),
    };
  };

  const normalizeItem = (item, index) => ({
    item_id:        item.item_id,
    item_name:      item.item_name || null,
    index:          index != null ? index : 0,
    item_brand:     'Dot & Key',
    item_category:  item.item_category || 'NA',
    item_list_id:   LIST_ID,
    item_list_name: LIST_NAME,
    item_variant:   item.item_variant || null,
    price:          toMajorUnits(item.price),
  });

  const pushEcommerceEvent = (eventName, ecommerce) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event:              eventName,
      client_id:          getClientId(),
      exp_variant_string: getExpVariantString(),
      item_list_id:       LIST_ID,
      item_list_name:     LIST_NAME,
      ecommerce,
    });
  };

  const pushSelectContentEvent = (action, item) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event:              'select_content',
      content_type:       'cab_bundle_item',
      action,
      item_id:            item.item_id,
      client_id:          getClientId(),
      exp_variant_string: getExpVariantString(),
      item_list_id:       LIST_ID,
      item_list_name:     LIST_NAME,
      item_name:          item.item_name,
      item_category:      item.item_category,
      item_variant:       item.item_variant,
      price:              toMajorUnits(item.price),
    });
  };

  // ── view_item_list ──────────────────────────────────────────────────────────
  const viewedSections   = new WeakSet();
  const observedSections = new WeakSet();

  const trackViewItemList = (section) => {
    if (viewedSections.has(section)) return;
    viewedSections.add(section);

    const validItems = Array.from(section.querySelectorAll(ITEM_SELECTOR))
      .map(cardToItem)
      .filter((item) => item.item_id);
    if (!validItems.length) return;

    pushEcommerceEvent('view_item_list', {
      items: validItems.map((item, index) => normalizeItem(item, index)),
    });
  };

  const observeSection = (section) => {
    if (observedSections.has(section) || typeof IntersectionObserver === 'undefined') return;
    observedSections.add(section);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].intersectionRatio < 1) return;
        observer.disconnect();
        trackViewItemList(section);
      },
      { threshold: 1 }
    );
    observer.observe(section);
  };

  const scanForSections = () =>
    document.querySelectorAll(SECTION_SELECTOR).forEach(observeSection);

  new MutationObserver(scanForSections).observe(document.body, { childList: true, subtree: true });
  scanForSections();

  // ── select_item + select_content ────────────────────────────────────────────
  document.addEventListener('click', (event) => {
    const section = event.target.closest(SECTION_SELECTOR);
    if (!section) return;

    const link = event.target.closest(
      '.customers-also-bought__title, .customers-also-bought__image-link'
    );
    if (link) {
      const card = link.closest(ITEM_SELECTOR);
      if (card) {
        const item = cardToItem(card);
        if (item.item_id) pushEcommerceEvent('select_item', { items: [normalizeItem(item)] });
      }
      return;
    }

    const variantBtn = event.target.closest('[data-cab-variant-btn]');
    if (variantBtn) {
      const card = variantBtn.closest(ITEM_SELECTOR);
      if (card) {
        const item = cardToItem(card);
        if (item.item_id) pushSelectContentEvent('variant_change', item);
      }
      return;
    }

    const checkboxBtn = event.target.closest('.customers-also-bought__checkbox-button');
    const card = checkboxBtn
      ? checkboxBtn.closest(ITEM_SELECTOR)
      : event.target.closest('[data-cab-variant-selector]')
        ? null
        : event.target.closest(ITEM_SELECTOR);
    if (!card) return;

    const item = cardToItem(card);
    if (!item.item_id) return;

    pushSelectContentEvent(card.classList.contains('is-active') ? 'select' : 'deselect', item);
  });

  // ── add_to_cart ─────────────────────────────────────────────────────────────
  let pendingAddToCart = null;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const formId = form && form.getAttribute('id');
    if (!formId || !formId.startsWith('customers-also-bought-form-')) return;

    const section = form.closest(SECTION_SELECTOR);
    if (!section) return;

    const selectedCards = Array.from(section.querySelectorAll(ITEM_SELECTOR))
      .filter((card) => card.classList.contains('is-active'));

    const items = selectedCards.map(cardToItem).filter((item) => item.item_id);
    if (!items.length) return;

    let value = items.reduce((sum, item) => sum + item.price, 0);
    if (window.DnkPricing && typeof window.DnkPricing.calcBundleTotal === 'function') {
      value = window.DnkPricing.calcBundleTotal(
        selectedCards.map((card) => ({
          variantId:   parseVariantId(card.dataset.variantId),
          price:       Number(card.dataset.price || 0),
          productType: (card.dataset.productType || '').trim(),
          productId:   card.dataset.productId || '',
        }))
      );
    }

    pendingAddToCart = { items, value };
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
      if (!data || data.source !== 'customers-also-bought' || !pendingAddToCart) return;

      const { items, value } = pendingAddToCart;
      pendingAddToCart = null;

      pushEcommerceEvent('add_to_cart', {
        currency: getCurrency(),
        value:    toMajorUnits(value),
        items:    items.map((item, index) => ({ ...normalizeItem(item, index), quantity: 1 })),
      });
    });
  }
})();